// CompanyMenuPanel.jsx — 테넌트 관리 › 회사 탭 › 회사 펼침 › '메뉴' (2026-10-19)
//   사용자 규칙: "테넌트 관리에서 테넌트에 부여할 메뉴를 정할 수 있다. 테넌트 관리에서 회사별로 부여할 메뉴를 정할 수 있다
//   (단, 속한 테넌트가 부여받은 메뉴 안에서)" — 운영사 회사(파인그룹 DOT·LNJ)도 같다.
//   - 보이는 메뉴 = 테넌트가 받은 메뉴(테넌트 탭에서 테넌트 줄을 눌러 정함)만. 그 밖의 메뉴는 여기서 켤 수 없다(서버도 409).
//   - 대표 회사 = 테넌트 메뉴가 그대로 적용 → 잠금.
//   - 직원에게 실제로 쓰게 하려면 업무/기능 배정에서 그 회사 열쇠를 준다.
//   예전 자리(업무/기능 배정 › 고객사 기능 배정 › 회사별 기능 제공 설정)는 없앴다 — 문은 하나.
//   읽기·저장 주소: /api/admin/saas/tenants/:code/company-modules (운영자 = 모든 테넌트, 테넌트 관리자 = 자기 테넌트).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiClient } from '../../../api/apiClient';
// 하드코딩 메뉴 = 연한 붉은색 + 한 줄 설명(2026-10-27) — **운영자 화면에서만**. 테넌트 관리자(forTenantAdmin)에게는
//   설명에 다른 테넌트 이름(파인그룹)이 들어 있어 보이지 않는다.
import { hardcodedNote, RED_CARD, RED_NOTE, redConfirmPrefix } from './hardcodedMenus';

function toggleStyle(on, disabled) {
  return {
    border: `1px solid ${on ? '#16a34a' : 'var(--border)'}`,
    background: on ? '#16a34a' : 'var(--bg-subtle)',
    color: on ? '#fff' : 'var(--text-sub)',
    borderRadius: 7,
    padding: '5px 10px',
    fontSize: 12,
    fontWeight: 800,
    minWidth: 64,
    cursor: disabled ? 'default' : 'pointer',
    opacity: disabled && !on ? 0.6 : 1,
    whiteSpace: 'nowrap',
  };
}

const rowStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  padding: '7px 10px',
  border: '1px solid var(--border)',
  borderRadius: 8,
  background: 'var(--bg-card)',
  minWidth: 0,
};

// forTenantAdmin: 테넌트 관리자 화면(업무/기능 배정) — 테넌트 관리 화면 안내 대신 '운영사가 정함'으로 쓴다.
export default function CompanyMenuPanel({ tenantCode, company, showToast, forTenantAdmin = false }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  const [savingCode, setSavingCode] = useState('');
  const companyCode = String(company?.code || '').toUpperCase();
  const isPrimary = company?.is_primary === true;

  const load = useCallback(async () => {
    if (!tenantCode || !companyCode) return;
    setError('');
    const res = await apiClient.get(`/api/admin/saas/tenants/${tenantCode}/company-modules`);
    if (!res.ok) {
      setRows([]);
      setError(res.data?.message || '회사 메뉴를 불러오지 못했습니다.');
      return;
    }
    const all = res.data?.report?.rows || [];
    setRows(all.filter((r) => String(r.company_code || '').toUpperCase() === companyCode));
  }, [tenantCode, companyCode]);
  useEffect(() => { load(); }, [load]);

  // 테넌트가 받은 메뉴만 — 대표 회사는 테넌트 메뉴 그대로(켜짐으로 보인다)
  const menus = useMemo(() => (rows || [])
    .filter((r) => r.tenant_budget_enabled === true)
    .map((r) => ({
      code: r.module_code,
      name: r.module_name || r.module_code,
      on: isPrimary ? true : r.company_enabled === true,
      assigned: Number(r.assigned_user_count || 0),
    })), [rows, isPrimary]);
  const onCount = menus.filter((m) => m.on).length;

  async function toggle(menu) {
    if (isPrimary || savingCode) return;
    const next = !menu.on;
    const msg = next
      ? `${forTenantAdmin ? '' : redConfirmPrefix(menu.code)}${company.external_name || company.name || companyCode} 에 '${menu.name}' 메뉴를 줄까요?\n\n직원이 쓰려면 업무/기능 배정에서 이 회사 열쇠를 따로 줘야 합니다.`
      : `${company.external_name || company.name || companyCode} 에서 '${menu.name}' 메뉴를 뺄까요?\n\n이 회사에서 이 메뉴가 사라집니다${menu.assigned ? `(지금 열쇠를 가진 직원 ${menu.assigned}명 포함)` : ''}. 기록은 지워지지 않습니다.`;
    if (!window.confirm(msg)) return;
    setSavingCode(menu.code);
    const res = await apiClient.put(`/api/admin/saas/tenants/${tenantCode}/company-modules`, {
      companyCode,
      moduleCode: menu.code,
      isEnabled: next,
      note: `[COMPANY_MENU] 테넌트 관리 회사 탭에서 ${next ? '제공' : '미제공'}으로 변경`,
    });
    setSavingCode('');
    if (!res.ok) { showToast?.(res.data?.message || '회사 메뉴 저장에 실패했습니다.', 'error'); return; }
    showToast?.(`'${menu.name}' 메뉴를 ${next ? '제공' : '미제공'}으로 저장했습니다.`, 'success');
    await load();
  }

  return (
    <div data-company-menu={companyCode}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 800 }}>메뉴</span>
        {rows && !error && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{onCount} / {menus.length} 제공 중</span>}
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8, lineHeight: 1.5 }}>
        {isPrimary
          ? (forTenantAdmin
            ? <>대표 회사는 운영사가 우리 테넌트에 준 메뉴가 그대로 적용됩니다.</>
            : <>대표 회사는 테넌트 메뉴가 그대로 적용됩니다. 바꾸려면 <strong>테넌트</strong> 탭에서 이 테넌트를 눌러 바꾸세요.</>)
          : <>테넌트가 받은 메뉴 안에서 이 회사에 줄 메뉴를 정합니다. 직원에게는 <strong>업무/기능 배정</strong>에서 이 회사 열쇠를 줍니다.</>}
      </div>
      {rows === null && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>불러오는 중…</div>}
      {error && <div style={{ fontSize: 12, color: '#dc2626' }}>{error}</div>}
      {rows && !error && menus.length === 0 && (
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{forTenantAdmin ? '운영사가 우리 테넌트에 준 메뉴가 없습니다.' : <>테넌트가 받은 메뉴가 없습니다. <strong>테넌트</strong> 탭에서 이 테넌트를 눌러 먼저 메뉴를 주세요.</>}</div>
      )}
      {menus.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 6 }}>
          {menus.map((m) => {
            const hcNote = forTenantAdmin ? '' : hardcodedNote(m.code);
            return (
            <div key={m.code} style={hcNote ? { ...rowStyle, ...RED_CARD } : rowStyle} data-menu-code={m.code} data-hardcoded={hcNote ? 'true' : undefined}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.name}</div>
                {hcNote && <div style={RED_NOTE}>{hcNote}</div>}
                {m.assigned > 0 && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>열쇠 받은 직원 {m.assigned}명</div>}
              </div>
              <button
                type="button"
                style={toggleStyle(m.on, isPrimary || savingCode === m.code)}
                disabled={isPrimary || savingCode === m.code}
                onClick={() => toggle(m)}
                aria-pressed={m.on}
              >
                {savingCode === m.code ? '저장 중' : m.on ? '제공 중' : '미제공'}
              </button>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
